<script lang="ts">
  /**
   * Översikts-karta för Rita-tabben (S-04-17c-part1 + jonas-feedback 2026-05-06).
   *
   * Visar admin-publicerade kart-objekt visuellt på MapLibre:
   *  - **Rundor** som linjer (`/api/routes/active`)
   *  - **Härd-segment** som streckade linjer per runda (`/api/admin/routes/{id}/hearths`)
   *  - **POI** som markers via `listPois()` → `/api/admin/map-pins`
   *
   * POI-läsning gick tidigare via walker-endpointen, men den har 60s
   * public cache vilket gjorde att nyss skapade pins inte syns på
   * kartan. Admin-endpointen är ocachad och returnerar full `PoiRecord`-
   * shape (samma som `PoisView` använder); klient-side filtrerar vi
   * `isPoiActive` så bilden matchar walker-vyn.
   *
   * Lager-toggles är checkboxes i toolbar — admin kan dölja respektive
   * lager för att fokusera på en typ.
   *
   * Klick-på-karta-för-att-skapa POI levereras i part2-B (denna iteration).
   */
  import type { Map as MapLibreMap, GeoJSONSource, MapMouseEvent } from 'maplibre-gl';
  import { onMount } from 'svelte';
  import nearestPointOnLine from '@turf/nearest-point-on-line';
  import lineSlice from '@turf/line-slice';
  import { lineString as turfLineString, point as turfPoint } from '@turf/helpers';
  import MapView from '../MapView.svelte';
  import PoiQuickCreateModal from './PoiQuickCreateModal.svelte';
  import HearthSegmentQuickCreateModal from './HearthSegmentQuickCreateModal.svelte';
  import { isActive as isPoiActive, listPois, type PoiRecord } from '../pois/poisClient';
  import { parseMultiLineStringWkt } from '../util/wkt';
  import { renderColorFor } from '../util/colorPalette';

  type Props = {
    initialCenter?: { lng: number; lat: number };
    initialZoom?: number;
    /** Anropas vid 401/403 så App-shellen tvingar logout. */
    onUnauthorized?: () => void;
    /**
     * Signal-counter från RitaView som triggar startAddPoi när admin
     * klickar lifted "+" i layer-toolbar med activeLayer=poi (UX-fix
     * 2026-05-05). Initial värde 0 ska INTE auto-trigga.
     */
    addPoiTrigger?: number;
    /**
     * Signal-counter från RitaView som triggar startAddSegment när admin
     * klickar lifted "+" i layer-toolbar med activeLayer=segment. Startar
     * pick-runda + två-punkter-flödet (Jonas-feedback 2026-05-05).
     * Initial värde 0 ska INTE auto-trigga.
     */
    addSegmentTrigger?: number;
    /**
     * Signal-counter från RitaView som triggar refresh när admin klickar
     * lifted refresh-ikon i layer-toolbar i karta-mode. Initial värde 0
     * ska INTE auto-trigga.
     */
    refreshTrigger?: number;
  };
  const {
    initialCenter,
    initialZoom,
    onUnauthorized,
    addPoiTrigger = 0,
    addSegmentTrigger = 0,
    refreshTrigger = 0,
  }: Props = $props();

  type RouteSegment = {
    id: string;
    routeId: string;
    name: string;
    geometryWkt: string;
    priority: 'low' | 'medium' | 'high';
  };

  type ActiveRoute = {
    id: string;
    name: string;
    priority: 'low' | 'medium' | 'high';
    geometry: Array<{ points: Array<{ latitude: number; longitude: number }> }>;
    /** S-04-24: kortnamn från palett-listan eller null för fallback. */
    displayColor: string | null;
  };

  let mapInstance: MapLibreMap | null = $state(null);
  let routes: ActiveRoute[] = $state([]);
  let segments: RouteSegment[] = $state([]);
  let pins: PoiRecord[] = $state([]);
  let dataError: string | null = $state(null);

  let showRundor = $state(true);
  let showHardSegment = $state(true);
  let showPoi = $state(true);
  // S-04-27: start/slut-markörer på rundor. Riktningen härleds från
  // koordinat-ordningen i geometrin (start = lines[0][0], slut =
  // lines[N-1].last). Default synlig så promenören ser rundan
  // riktning.
  let showEndpoints = $state(true);

  // Klick-att-skapa-POI-flöde (S-04-17c-part2-B). När admin klickat på
  // "Lägg till POI" går vi i `addPoiMode` — kart-cursor blir crosshair
  // och nästa klick öppnar quick-form-modalen med koordinaterna pre-
  // fyllda. Cancel/escape avbryter mode utan att skapa något.
  let addPoiMode = $state(false);
  let pendingPoiLat: number | null = $state(null);
  let pendingPoiLng: number | null = $state(null);

  // Klick-att-skapa-härd-segment-flöde (Jonas-feedback 2026-05-05).
  // State-maskin med fyra steg:
  //  - 'idle'        — inte i segment-flöde
  //  - 'pickRunda'   — admin ska klicka på en runda för att välja den
  //  - 'pickStart'   — runda vald, admin ska klicka start-punkt
  //  - 'pickEnd'     — start vald, admin ska klicka slut-punkt
  //  - 'preview'     — slut vald, modal öppen för namn + prioritet
  // Esc avbryter alltid och återställer till 'idle'.
  type SegmentFlowState = 'idle' | 'pickRunda' | 'pickStart' | 'pickEnd' | 'preview';
  let segmentFlow: SegmentFlowState = $state('idle');
  let segmentRouteId: string | null = $state(null);
  let segmentRouteName: string = $state('');
  // Linje-koordinater för vald runda i [lng, lat]-format. Backend
  // representerar rundor som MultiLineString men för pick-flow flatten:as
  // alla linjer till en sammanhängande sekvens (rundor är typiskt en
  // single-line, så detta är safe — fallback för flersegmentade rundor
  // räknar slice korrekt på den första matchande linjen).
  let segmentRouteCoords: Array<[number, number]> = $state([]);
  let segmentStartLng: number | null = $state(null);
  let segmentStartLat: number | null = $state(null);
  let segmentEndLng: number | null = $state(null);
  let segmentEndLat: number | null = $state(null);
  let segmentSliceWkt: string | null = $state(null);

  const ROUTES_SOURCE = 'rita-routes';
  const ROUTES_LAYER = 'rita-routes-line';
  const SEGMENTS_SOURCE = 'rita-segments';
  const SEGMENTS_LAYER = 'rita-segments-line';
  const POI_SOURCE = 'rita-pois';
  const POI_LAYER = 'rita-pois-circle';
  const ENDPOINTS_SOURCE = 'rita-route-endpoints';
  const ENDPOINTS_LAYER = 'rita-route-endpoints-circle';
  // Källor + lager för segment-flödet (synliga endast under flödet).
  const PICK_POINTS_SOURCE = 'rita-segment-pick-points';
  const PICK_POINTS_LAYER = 'rita-segment-pick-points-circle';
  const SLICE_PREVIEW_SOURCE = 'rita-segment-slice-preview';
  const SLICE_PREVIEW_LAYER = 'rita-segment-slice-preview-line';


  async function fetchRoutes(): Promise<void> {
    try {
      const response = await fetch('/api/routes/active', {
        credentials: 'include',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        if (response.status !== 401) {
          dataError = 'Kunde inte hämta rundor.';
        }
        routes = [];
        return;
      }
      const body = (await response.json()) as {
        hearths?: Array<
          Omit<ActiveRoute, 'displayColor'> & {
            display_color?: string | null;
            displayColor?: string | null;
          }
        >;
      };
      // S-04-24: backend kan svara med camelCase eller snake_case
      // beroende på vilken endpoint som svarar (admin vs walker).
      // Acceptera båda för robusthet.
      routes = (body.hearths ?? []).map((r) => ({
        id: r.id,
        name: r.name,
        priority: r.priority,
        geometry: r.geometry,
        displayColor:
          (typeof r.displayColor === 'string' ? r.displayColor : null) ??
          (typeof r.display_color === 'string' ? r.display_color : null),
      }));
    } catch {
      dataError = 'Nätverksfel vid hämtning av rundor.';
      routes = [];
    }
  }

  /**
   * Fetcha alla härd-segment via admin-endpoint per runda. För
   * pilot-skala (5-10 rundor) är N+1-fetch acceptabelt — för skala
   * post-MVP1 bör en aggregerad endpoint introduceras (t.ex.
   * /api/admin/segments eller /api/walker/segments?bbox=...).
   */
  async function fetchSegments(routeIds: ReadonlyArray<string>): Promise<void> {
    try {
      const allSegments: RouteSegment[] = [];
      const responses = await Promise.all(
        routeIds.map((id) =>
          fetch(`/api/admin/routes/${encodeURIComponent(id)}/hearths`, {
            credentials: 'include',
            headers: { Accept: 'application/json' },
          }).catch(() => null),
        ),
      );
      for (let i = 0; i < responses.length; i++) {
        const response = responses[i];
        if (!response || !response.ok) continue;
        const body = (await response.json()) as {
          items?: Array<{
            id: string;
            route_id: string;
            name: string;
            geometry_wkt: string;
            priority: 'low' | 'medium' | 'high';
          }>;
        };
        for (const item of body.items ?? []) {
          allSegments.push({
            id: item.id,
            routeId: item.route_id,
            name: item.name,
            geometryWkt: item.geometry_wkt,
            priority: item.priority,
          });
        }
      }
      segments = allSegments;
    } catch {
      segments = [];
    }
  }

  async function fetchPins(): Promise<void> {
    // Admin-endpointen `/api/admin/map-pins` används istället för
    // walker-endpointen `/api/walker/map-pins` (jonas-feedback 2026-05-06):
    // walker-endpointen sätter `Cache-Control: public, max-age=60` vilket
    // gör att en nyss skapad POI inte syns på kartan på upp till 60s
    // efter post — admin tror create misslyckades. Admin-endpointen
    // saknar cache och returnerar PoiRecord-shape som resten av poi-
    // stacken redan använder. Klient-side filtrering på `deletedAt` +
    // active-period gör att bilden matchar walker-vyn.
    const result = await listPois();
    if (result.kind === 'ok') {
      // isPoiActive testar redan deletedAt + active-period. Wrapper-
      // arrow är medveten — `isPoiActive(record, now?: Date)` kan inte
      // skickas direkt till Array.filter eftersom filter:s callback
      // skickar `(value, index, array)` och TS-strict avvisar
      // `index: number ≠ Date | undefined`.
      pins = result.items.filter((p) => isPoiActive(p));
      return;
    }
    pins = [];
    if (result.kind === 'unauthorized') {
      onUnauthorized?.();
      return;
    }
    if (result.kind === 'forbidden') {
      dataError = 'Saknar behörighet att se POI.';
      return;
    }
    dataError = result.message;
  }

  function routesAsGeoJson() {
    return {
      type: 'FeatureCollection' as const,
      features: routes.flatMap((r) =>
        r.geometry.map((line) => ({
          type: 'Feature' as const,
          properties: {
            id: r.id,
            name: r.name,
            priority: r.priority,
            // S-04-24b: line-color använder display_color med priority-
            // fallback. Pre-beräknat klient-side så MapLibre inte behöver
            // matcha mot palett-namn.
            color: renderColorFor(r.displayColor, r.priority),
          },
          geometry: {
            type: 'LineString' as const,
            coordinates: line.points.map((p) => [p.longitude, p.latitude]),
          },
        })),
      ),
    };
  }

  function segmentsAsGeoJson() {
    return {
      type: 'FeatureCollection' as const,
      features: segments.flatMap((s) => {
        const lines = parseMultiLineStringWkt(s.geometryWkt);
        return lines.map((coords) => ({
          type: 'Feature' as const,
          properties: {
            id: s.id,
            routeId: s.routeId,
            name: s.name,
            priority: s.priority,
          },
          geometry: {
            type: 'LineString' as const,
            coordinates: coords,
          },
        }));
      }),
    };
  }

  /**
   * S-04-27: bygger Point-features för rundans start- och slut-punkter.
   * Riktningen härleds från koordinat-ordningen i `geometry`:
   *   start = geometry[0].points[0]
   *   slut  = geometry[N-1].points.last
   * Kräver inget schema-arbete — PostGIS bevarar koord-ordningen vid
   * lagring och GraphHopper-snap returnerar polylinen i input-ordning.
   * Edit-flow får INTE rotera koord-arrayen (regel i HearthsView).
   */
  function endpointsAsGeoJson() {
    return {
      type: 'FeatureCollection' as const,
      features: routes.flatMap((r) => {
        if (r.geometry.length === 0) return [];
        const firstLine = r.geometry[0];
        const lastLine = r.geometry[r.geometry.length - 1];
        if (firstLine.points.length === 0 || lastLine.points.length === 0) return [];
        const start = firstLine.points[0];
        const end = lastLine.points[lastLine.points.length - 1];
        return [
          {
            type: 'Feature' as const,
            properties: {
              kind: 'start',
              routeId: r.id,
              name: r.name,
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [start.longitude, start.latitude],
            },
          },
          {
            type: 'Feature' as const,
            properties: {
              kind: 'end',
              routeId: r.id,
              name: r.name,
            },
            geometry: {
              type: 'Point' as const,
              coordinates: [end.longitude, end.latitude],
            },
          },
        ];
      }),
    };
  }

  function pinsAsGeoJson() {
    return {
      type: 'FeatureCollection' as const,
      features: pins.map((p) => ({
        type: 'Feature' as const,
        properties: {
          id: p.id,
          title: p.title,
          pin_type: p.pinType,
          kind: p.kind,
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [p.longitude, p.latitude],
        },
      })),
    };
  }

  function ensureRouteLayer(map: MapLibreMap): void {
    if (!map.getSource(ROUTES_SOURCE)) {
      map.addSource(ROUTES_SOURCE, {
        type: 'geojson',
        data: routesAsGeoJson(),
      });
    } else {
      const src = map.getSource(ROUTES_SOURCE) as GeoJSONSource;
      src.setData(routesAsGeoJson());
    }
    if (!map.getLayer(ROUTES_LAYER)) {
      map.addLayer({
        id: ROUTES_LAYER,
        type: 'line',
        source: ROUTES_SOURCE,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
          'line-opacity': 0.85,
        },
      });
    }
  }

  function ensureSegmentLayer(map: MapLibreMap): void {
    if (!map.getSource(SEGMENTS_SOURCE)) {
      map.addSource(SEGMENTS_SOURCE, {
        type: 'geojson',
        data: segmentsAsGeoJson(),
      });
    } else {
      const src = map.getSource(SEGMENTS_SOURCE) as GeoJSONSource;
      src.setData(segmentsAsGeoJson());
    }
    if (!map.getLayer(SEGMENTS_LAYER)) {
      // Distinkt visuell stil från rundor: varm glow-vit, bredare än
      // rundans linje (4px), dashed för att tydligt visa "del av en
      // runda". Bredden gör segmentet till en synlig delmängd ovanpå
      // rundan utan att konkurrera om färgschemat (jonas-feedback
      // 2026-05-06: tidigare 2.5px ljus guld var för svag).
      map.addLayer({
        id: SEGMENTS_LAYER,
        type: 'line',
        source: SEGMENTS_SOURCE,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#fff4d6',
          'line-width': 5,
          'line-opacity': 0.95,
          'line-dasharray': [3, 2],
        },
      });
    }
  }

  function ensureEndpointsLayer(map: MapLibreMap): void {
    if (!map.getSource(ENDPOINTS_SOURCE)) {
      map.addSource(ENDPOINTS_SOURCE, {
        type: 'geojson',
        data: endpointsAsGeoJson(),
      });
    } else {
      const src = map.getSource(ENDPOINTS_SOURCE) as GeoJSONSource;
      src.setData(endpointsAsGeoJson());
    }
    if (!map.getLayer(ENDPOINTS_LAYER)) {
      // Visuell distinktion: start är större + ljusare glow-gul,
      // slut är mindre + dämpad accent-brun. Ringen i bakgrunds-
      // svart ger tydlig kontrast mot den mörka vector-basen.
      // Ingen text-symbol — krav på font-glyphs i Protomaps är
      // osäkert; storlek + färg räcker som visuell riktnings-
      // indikator.
      map.addLayer({
        id: ENDPOINTS_LAYER,
        type: 'circle',
        source: ENDPOINTS_SOURCE,
        paint: {
          'circle-radius': ['case', ['==', ['get', 'kind'], 'start'], 9, 7],
          'circle-color': [
            'case',
            ['==', ['get', 'kind'], 'start'],
            '#f4d589',
            '#a07a40',
          ],
          'circle-stroke-color': '#0e0e10',
          'circle-stroke-width': 2,
          'circle-opacity': 0.95,
        },
      });
    }
  }

  function ensurePoiLayer(map: MapLibreMap): void {
    if (!map.getSource(POI_SOURCE)) {
      map.addSource(POI_SOURCE, {
        type: 'geojson',
        data: pinsAsGeoJson(),
      });
    } else {
      const src = map.getSource(POI_SOURCE) as GeoJSONSource;
      src.setData(pinsAsGeoJson());
    }
    if (!map.getLayer(POI_LAYER)) {
      map.addLayer({
        id: POI_LAYER,
        type: 'circle',
        source: POI_SOURCE,
        paint: {
          'circle-radius': 6,
          'circle-color': '#d4a056',
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      });
    }
  }

  function applyVisibility(map: MapLibreMap): void {
    if (map.getLayer(ROUTES_LAYER)) {
      map.setLayoutProperty(ROUTES_LAYER, 'visibility', showRundor ? 'visible' : 'none');
    }
    if (map.getLayer(SEGMENTS_LAYER)) {
      map.setLayoutProperty(SEGMENTS_LAYER, 'visibility', showHardSegment ? 'visible' : 'none');
    }
    if (map.getLayer(POI_LAYER)) {
      map.setLayoutProperty(POI_LAYER, 'visibility', showPoi ? 'visible' : 'none');
    }
    if (map.getLayer(ENDPOINTS_LAYER)) {
      // Start/slut-markörer följer Rundor-toggles eftersom de är
      // semantiskt en del av rundans visualisering — utan rundan
      // finns ingen kontext för markörerna. Plus en separat toggle
      // som låter admin gömma dem för rena översiktsvyer.
      const visible = showRundor && showEndpoints;
      map.setLayoutProperty(ENDPOINTS_LAYER, 'visibility', visible ? 'visible' : 'none');
    }
  }

  function handleMapClick(event: MapMouseEvent): void {
    if (addPoiMode) {
      pendingPoiLat = event.lngLat.lat;
      pendingPoiLng = event.lngLat.lng;
      addPoiMode = false;
      if (mapInstance) {
        mapInstance.getCanvas().style.cursor = '';
      }
      return;
    }
    if (segmentFlow !== 'idle') {
      handleSegmentFlowClick(event);
    }
  }

  /**
   * Routar klick under härd-segment-flödet beroende på state.
   *  - pickRunda: queryRenderedFeatures för att hitta klickad runda; om
   *    en runda hits sparas dess id + koordinater och vi går till
   *    pickStart. Om ingen hit — banner uppdateras inte, klicket ignoreras.
   *  - pickStart: snap:ar klickade lng/lat till närmaste punkt på rundans
   *    linje (turf nearestPointOnLine). Visualiserar punkten och går
   *    till pickEnd.
   *  - pickEnd: snap:ar och beräknar slice mellan start och slut. Sparar
   *    WKT och öppnar modal (state -> preview).
   */
  function handleSegmentFlowClick(event: MapMouseEvent): void {
    if (!mapInstance) return;
    if (segmentFlow === 'pickRunda') {
      const features = mapInstance.queryRenderedFeatures(event.point, {
        layers: [ROUTES_LAYER],
      });
      if (features.length === 0) return;
      const feature = features[0];
      const props = feature.properties ?? {};
      const routeId = typeof props.id === 'string' ? props.id : null;
      if (!routeId) return;
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;
      segmentRouteId = route.id;
      segmentRouteName = route.name;
      segmentRouteCoords = flattenRouteCoords(route);
      segmentFlow = 'pickStart';
      updatePickPointsSource();
      updateSlicePreviewSource();
      return;
    }
    if (segmentFlow === 'pickStart') {
      if (segmentRouteCoords.length < 2) return;
      const snapped = snapToRoute(event.lngLat.lng, event.lngLat.lat);
      if (!snapped) return;
      segmentStartLng = snapped[0];
      segmentStartLat = snapped[1];
      segmentFlow = 'pickEnd';
      updatePickPointsSource();
      return;
    }
    if (segmentFlow === 'pickEnd') {
      if (segmentRouteCoords.length < 2) return;
      const snapped = snapToRoute(event.lngLat.lng, event.lngLat.lat);
      if (!snapped) return;
      segmentEndLng = snapped[0];
      segmentEndLat = snapped[1];
      const wkt = buildSliceWkt();
      if (!wkt) {
        // Slice misslyckades — återställ till pickStart så admin kan
        // försöka igen utan att lämna flödet.
        segmentEndLng = null;
        segmentEndLat = null;
        return;
      }
      segmentSliceWkt = wkt;
      segmentFlow = 'preview';
      updatePickPointsSource();
      updateSlicePreviewSource();
    }
  }

  function flattenRouteCoords(route: ActiveRoute): Array<[number, number]> {
    const coords: Array<[number, number]> = [];
    for (const line of route.geometry) {
      for (const p of line.points) {
        coords.push([p.longitude, p.latitude]);
      }
    }
    return coords;
  }

  function snapToRoute(lng: number, lat: number): [number, number] | null {
    if (segmentRouteCoords.length < 2) return null;
    try {
      const lineFeature = turfLineString(segmentRouteCoords);
      const clickPoint = turfPoint([lng, lat]);
      const snap = nearestPointOnLine(lineFeature, clickPoint);
      const c = snap.geometry.coordinates as [number, number];
      return [c[0], c[1]];
    } catch {
      return null;
    }
  }

  function buildSliceWkt(): string | null {
    if (
      segmentStartLng === null ||
      segmentStartLat === null ||
      segmentEndLng === null ||
      segmentEndLat === null
    ) {
      return null;
    }
    if (segmentRouteCoords.length < 2) return null;
    try {
      const lineFeature = turfLineString(segmentRouteCoords);
      const startPt = turfPoint([segmentStartLng, segmentStartLat]);
      const endPt = turfPoint([segmentEndLng, segmentEndLat]);
      const slice = lineSlice(startPt, endPt, lineFeature);
      const coords = slice.geometry.coordinates as Array<[number, number]>;
      if (coords.length < 2) return null;
      // Backend kräver MULTILINESTRING (V202604270003). Wrappa slice:n
      // som single-linje inom multi-line-strukturen.
      const inner = coords.map((c) => `${c[0]} ${c[1]}`).join(', ');
      return `MULTILINESTRING((${inner}))`;
    } catch {
      return null;
    }
  }

  function startAddSegment(): void {
    resetSegmentFlow();
    segmentFlow = 'pickRunda';
  }

  function cancelAddSegment(): void {
    resetSegmentFlow();
  }

  function resetSegmentFlow(): void {
    segmentFlow = 'idle';
    segmentRouteId = null;
    segmentRouteName = '';
    segmentRouteCoords = [];
    segmentStartLng = null;
    segmentStartLat = null;
    segmentEndLng = null;
    segmentEndLat = null;
    segmentSliceWkt = null;
    updatePickPointsSource();
    updateSlicePreviewSource();
  }

  async function handleSegmentCreated(): Promise<void> {
    const previousRouteId = segmentRouteId;
    resetSegmentFlow();
    // Ladda om segment så det nya syns omedelbart. Behåller övriga lager.
    if (previousRouteId) {
      await fetchSegments(routes.map((r) => r.id));
      if (mapInstance) {
        ensureSegmentLayer(mapInstance);
        applyVisibility(mapInstance);
      }
    }
  }

  function pickPointsAsGeoJson() {
    const features: Array<{
      type: 'Feature';
      properties: { kind: 'start' | 'end' };
      geometry: { type: 'Point'; coordinates: [number, number] };
    }> = [];
    if (segmentStartLng !== null && segmentStartLat !== null) {
      features.push({
        type: 'Feature',
        properties: { kind: 'start' },
        geometry: { type: 'Point', coordinates: [segmentStartLng, segmentStartLat] },
      });
    }
    if (segmentEndLng !== null && segmentEndLat !== null) {
      features.push({
        type: 'Feature',
        properties: { kind: 'end' },
        geometry: { type: 'Point', coordinates: [segmentEndLng, segmentEndLat] },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }

  function slicePreviewAsGeoJson() {
    if (!segmentSliceWkt) {
      return { type: 'FeatureCollection' as const, features: [] };
    }
    const lines = parseMultiLineStringWkt(segmentSliceWkt);
    return {
      type: 'FeatureCollection' as const,
      features: lines.map((coords) => ({
        type: 'Feature' as const,
        properties: {},
        geometry: { type: 'LineString' as const, coordinates: coords },
      })),
    };
  }

  function updatePickPointsSource(): void {
    if (!mapInstance) return;
    if (mapInstance.getSource(PICK_POINTS_SOURCE)) {
      const src = mapInstance.getSource(PICK_POINTS_SOURCE) as GeoJSONSource;
      src.setData(pickPointsAsGeoJson());
    }
  }

  function updateSlicePreviewSource(): void {
    if (!mapInstance) return;
    if (mapInstance.getSource(SLICE_PREVIEW_SOURCE)) {
      const src = mapInstance.getSource(SLICE_PREVIEW_SOURCE) as GeoJSONSource;
      src.setData(slicePreviewAsGeoJson());
    }
  }

  function ensurePickPointsLayer(map: MapLibreMap): void {
    if (!map.getSource(PICK_POINTS_SOURCE)) {
      map.addSource(PICK_POINTS_SOURCE, {
        type: 'geojson',
        data: pickPointsAsGeoJson(),
      });
    }
    if (!map.getLayer(PICK_POINTS_LAYER)) {
      map.addLayer({
        id: PICK_POINTS_LAYER,
        type: 'circle',
        source: PICK_POINTS_SOURCE,
        paint: {
          'circle-radius': 8,
          'circle-color': ['case', ['==', ['get', 'kind'], 'start'], '#f4d589', '#d4a056'],
          'circle-stroke-color': '#0e0e10',
          'circle-stroke-width': 2,
          'circle-opacity': 0.95,
        },
      });
    }
  }

  function ensureSlicePreviewLayer(map: MapLibreMap): void {
    if (!map.getSource(SLICE_PREVIEW_SOURCE)) {
      map.addSource(SLICE_PREVIEW_SOURCE, {
        type: 'geojson',
        data: slicePreviewAsGeoJson(),
      });
    }
    if (!map.getLayer(SLICE_PREVIEW_LAYER)) {
      map.addLayer({
        id: SLICE_PREVIEW_LAYER,
        type: 'line',
        source: SLICE_PREVIEW_SOURCE,
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#f4d589',
          'line-width': 6,
          'line-opacity': 0.85,
        },
      });
    }
  }

  /**
   * Banner-text som vägleder admin genom segment-flödet. Konsistent
   * stil med POI-banner ("Klicka på kartan…").
   */
  let segmentBannerText = $derived.by((): string => {
    switch (segmentFlow) {
      case 'pickRunda':
        return 'Klicka på en runda för att välja den.';
      case 'pickStart':
        return `Vald runda: ${segmentRouteName}. Klicka på startpunkt för segmentet.`;
      case 'pickEnd':
        return `Klicka på slutpunkt för segmentet på ${segmentRouteName}.`;
      case 'preview':
        return `Granska segmentet på ${segmentRouteName} och spara.`;
      default:
        return '';
    }
  });

  /**
   * Filter på rundor-laget under segment-flödet: när en runda är vald
   * dimmas övriga rundor genom paint-property `line-opacity` som baseras
   * på "vald runda eller ej". Implementerat via $effect som sätter
   * paint-property dynamiskt.
   */
  $effect(() => {
    if (!mapInstance) return;
    if (!mapInstance.getLayer(ROUTES_LAYER)) return;
    if (segmentRouteId && segmentFlow !== 'idle') {
      mapInstance.setPaintProperty(ROUTES_LAYER, 'line-opacity', [
        'case',
        ['==', ['get', 'id'], segmentRouteId],
        0.95,
        0.25,
      ]);
    } else {
      mapInstance.setPaintProperty(ROUTES_LAYER, 'line-opacity', 0.85);
    }
  });

  function handleMapReady(map: MapLibreMap | null): void {
    mapInstance = map;
    if (map) {
      // Fetch och render. Layers byggs efter att style är load:ad
      // (MapView triggar onMapReady efter map.once('load')).
      void fetchRoutes().then(() => {
        if (!mapInstance) return;
        ensureRouteLayer(mapInstance);
        // S-04-27: endpoints-layer tillsammans med rundor-laget
        // eftersom båda härleds från `routes`-data.
        ensureEndpointsLayer(mapInstance);
        applyVisibility(mapInstance);
        // Efter rundor är hämtade — fetcha segment per runda i parallel.
        void fetchSegments(routes.map((r) => r.id)).then(() => {
          if (mapInstance) {
            ensureSegmentLayer(mapInstance);
            applyVisibility(mapInstance);
          }
        });
      });
      void fetchPins().then(() => {
        if (mapInstance) {
          ensurePoiLayer(mapInstance);
          applyVisibility(mapInstance);
        }
      });
      // Pick-points + slice-preview-lager skapas tidigt med tom data så
      // segment-flödet bara behöver kalla updateXSource() vid state-byten.
      ensurePickPointsLayer(map);
      ensureSlicePreviewLayer(map);

      // Klick-handler för "Lägg till POI"-flödet och segment-flödet.
      map.on('click', handleMapClick);
    }
  }

  $effect(() => {
    // Sätter cursor till crosshair när admin är i addPoiMode eller i
    // ett aktivt segment-flöde (utom 'preview' där modal blockerar).
    if (mapInstance) {
      const wantCrosshair =
        addPoiMode ||
        segmentFlow === 'pickRunda' ||
        segmentFlow === 'pickStart' ||
        segmentFlow === 'pickEnd';
      mapInstance.getCanvas().style.cursor = wantCrosshair ? 'crosshair' : '';
    }
  });

  function startAddPoi(): void {
    addPoiMode = true;
  }

  function cancelAddPoi(): void {
    addPoiMode = false;
    pendingPoiLat = null;
    pendingPoiLng = null;
  }

  // Signal-counter-bryggor: när RitaView's lifted "+" klickas i karta-
  // mode triggas addPoiTrigger (poi-lager) eller addSegmentTrigger
  // (segment-lager); när lifted refresh klickas triggas refreshTrigger.
  // Initial 0 ska inte auto-fira (vakt: skip om lastSeen är undefined
  // eller lika med inkommande).
  let lastAddPoiTrigger: number | undefined = $state(undefined);
  let lastAddSegmentTrigger: number | undefined = $state(undefined);
  let lastRefreshTrigger: number | undefined = $state(undefined);
  $effect(() => {
    if (lastAddPoiTrigger === undefined) {
      lastAddPoiTrigger = addPoiTrigger;
      return;
    }
    if (addPoiTrigger !== lastAddPoiTrigger) {
      lastAddPoiTrigger = addPoiTrigger;
      startAddPoi();
    }
  });
  $effect(() => {
    if (lastAddSegmentTrigger === undefined) {
      lastAddSegmentTrigger = addSegmentTrigger;
      return;
    }
    if (addSegmentTrigger !== lastAddSegmentTrigger) {
      lastAddSegmentTrigger = addSegmentTrigger;
      startAddSegment();
    }
  });
  $effect(() => {
    if (lastRefreshTrigger === undefined) {
      lastRefreshTrigger = refreshTrigger;
      return;
    }
    if (refreshTrigger !== lastRefreshTrigger) {
      lastRefreshTrigger = refreshTrigger;
      void refresh();
    }
  });

  async function handlePoiCreated(): Promise<void> {
    pendingPoiLat = null;
    pendingPoiLng = null;
    // Refetch pins så den nya POI:n syns omedelbart
    await fetchPins();
  }

  $effect(() => {
    // Re-applicera synlighet när toggles ändras.
    if (mapInstance) {
      applyVisibility(mapInstance);
    }
  });

  $effect(() => {
    // Re-renderar GeoJSON-source när data laddats om. Toggles via
    // `routes`, `segments` och `pins` triggar denna automatiskt.
    if (!mapInstance) return;
    if (mapInstance.getSource(ROUTES_SOURCE)) {
      const src = mapInstance.getSource(ROUTES_SOURCE) as GeoJSONSource;
      src.setData(routesAsGeoJson());
    }
    if (mapInstance.getSource(ENDPOINTS_SOURCE)) {
      const src = mapInstance.getSource(ENDPOINTS_SOURCE) as GeoJSONSource;
      src.setData(endpointsAsGeoJson());
    }
    if (mapInstance.getSource(SEGMENTS_SOURCE)) {
      const src = mapInstance.getSource(SEGMENTS_SOURCE) as GeoJSONSource;
      src.setData(segmentsAsGeoJson());
    }
    if (mapInstance.getSource(POI_SOURCE)) {
      const src = mapInstance.getSource(POI_SOURCE) as GeoJSONSource;
      src.setData(pinsAsGeoJson());
    }
  });

  async function refresh(): Promise<void> {
    dataError = null;
    await Promise.all([fetchRoutes(), fetchPins()]);
    // fetchSegments måste vänta på rundor — kör efter routes-fetch är klar
    await fetchSegments(routes.map((r) => r.id));
  }

  onMount(() => {
    // No-op — MapView triggar handleMapReady som driver fetch + layer-setup.
  });

  function handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (addPoiMode) {
        cancelAddPoi();
      } else if (segmentFlow !== 'idle') {
        cancelAddSegment();
      }
    }
  }
</script>

<svelte:window onkeydown={handleKeyDown} />

<section class="rita-map">
  <header class="map-toolbar">
    <div class="layer-toggles">
      <label class="toggle">
        <input type="checkbox" bind:checked={showRundor} />
        <span class="dot route-dot" aria-hidden="true"></span>
        <span>Rundor ({routes.length})</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={showHardSegment} />
        <span class="dot segment-dot" aria-hidden="true"></span>
        <span>Härd-segment ({segments.length})</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={showPoi} />
        <span class="dot poi-dot" aria-hidden="true"></span>
        <span>POI ({pins.length})</span>
      </label>
      <label class="toggle">
        <input type="checkbox" bind:checked={showEndpoints} />
        <span class="dot endpoints-dot" aria-hidden="true"></span>
        <span>Start/slut</span>
      </label>
    </div>
    {#if addPoiMode}
      <div class="toolbar-actions">
        <button type="button" class="add-poi-btn cancel" onclick={cancelAddPoi}>
          Avbryt POI
        </button>
      </div>
    {/if}
    {#if segmentFlow !== 'idle' && segmentFlow !== 'preview'}
      <div class="toolbar-actions">
        <button type="button" class="add-poi-btn cancel" onclick={cancelAddSegment}>
          Avbryt segment
        </button>
      </div>
    {/if}
  </header>

  {#if addPoiMode}
    <div class="banner" role="status" aria-live="polite">
      <strong>Klicka på kartan</strong> där POI:n ska placeras. Tryck Esc eller "Avbryt POI" för att lämna
      läget.
    </div>
  {/if}

  {#if segmentFlow !== 'idle' && segmentFlow !== 'preview'}
    <div class="banner" role="status" aria-live="polite">
      <strong>Härd-segment:</strong>
      {segmentBannerText} Tryck Esc eller "Avbryt segment" för att lämna läget.
    </div>
  {/if}

  {#if dataError}
    <div class="alert error" role="alert">{dataError}</div>
  {/if}

  <div class="map-host">
    <MapView onMapReady={handleMapReady} {initialCenter} {initialZoom} />
  </div>

  <p class="hint dim">
    Översikt över aktiva rundor och POI:er. För att skapa eller redigera — välj lager ovanför och
    tryck +-knappen, eller växla till Lista-mode för CRUD-vy.
  </p>

  {#if pendingPoiLat !== null && pendingPoiLng !== null}
    <PoiQuickCreateModal
      latitude={pendingPoiLat}
      longitude={pendingPoiLng}
      onCreated={() => void handlePoiCreated()}
      onCancel={cancelAddPoi}
      {onUnauthorized}
    />
  {/if}

  {#if segmentFlow === 'preview' && segmentRouteId && segmentSliceWkt}
    <HearthSegmentQuickCreateModal
      routeId={segmentRouteId}
      routeName={segmentRouteName}
      geometryWkt={segmentSliceWkt}
      onCreated={() => void handleSegmentCreated()}
      onCancel={cancelAddSegment}
      {onUnauthorized}
    />
  {/if}
</section>

<style>
  .rita-map {
    display: grid;
    gap: 0.75rem;
  }
  .map-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--rule);
  }
  .layer-toggles {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    color: var(--text);
    font-size: 0.85rem;
    cursor: pointer;
  }
  .toggle.disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .toggle input {
    cursor: inherit;
  }
  .dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
  }
  .route-dot {
    background: #d4a056;
  }
  .segment-dot {
    background: #fff4d6;
    border: 1px solid var(--rule);
  }
  .poi-dot {
    background: #d4a056;
    border: 1.5px solid #ffffff;
    box-shadow: 0 0 0 1px var(--rule);
  }
  /* S-04-27: två-tons-prick som hintar om start (ljus) + slut (mörk).
     Linjär gradient mellan glow-gul och accent-brun. */
  .endpoints-dot {
    background: linear-gradient(90deg, #f4d589 0%, #f4d589 50%, #a07a40 50%, #a07a40 100%);
    border: 1px solid #0e0e10;
  }
  .toolbar-actions {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
  }
  .add-poi-btn {
    background: var(--accent);
    border: 1px solid var(--accent);
    color: var(--bg);
    padding: 0.4rem 0.85rem;
    border-radius: 4px;
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
  }
  .add-poi-btn:hover {
    background: var(--glow);
    border-color: var(--glow);
  }
  .add-poi-btn.cancel {
    background: var(--danger-soft);
    border-color: var(--danger);
    color: var(--text);
  }
  .add-poi-btn.cancel:hover {
    color: var(--glow);
  }
  .banner {
    padding: 0.65rem 0.9rem;
    background: var(--accent-soft);
    border: 1px solid var(--accent);
    border-radius: 4px;
    font-size: 0.9rem;
    color: var(--text);
  }
  .banner strong {
    color: var(--glow);
  }
  .map-host {
    width: 100%;
    height: 60vh;
    min-height: 420px;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid var(--rule);
  }
  .alert {
    padding: 0.55rem 0.8rem;
    border-radius: 4px;
    font-size: 0.85rem;
  }
  .alert.error {
    background: var(--danger-soft);
    border: 1px solid var(--danger);
    color: var(--text);
  }
  .hint {
    font-size: 0.8rem;
  }
  .dim {
    color: var(--text-dim);
  }
</style>
